import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "../components/ui/sonner";
import { UploadCloud } from "lucide-react";

export default function Historique() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [comparison, setComparison] = useState([]);

  useEffect(() => {
    api.get("/sites").then((res) => setSites(res.data));
  }, []);

  useEffect(() => {
    if (siteId) api.get(`/comparison/site/${siteId}`).then((res) => setComparison(res.data));
  }, [siteId]);

  const runPreview = async () => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/import-2024/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
    } catch {
      toast.error("Échec de l'analyse du fichier");
    }
  };

  const confirmImport = async () => {
    if (!siteId || !preview) return;
    const form = new FormData();
    form.append("site_id", siteId);
    form.append("filename", file?.name || "");
    form.append("rows", JSON.stringify(preview.rows));
    try {
      const res = await api.post("/import-2024/confirm", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.imported} réponses importées`);
      setPreview(null);
      setFile(null);
    } catch {
      toast.error("Échec de l'import");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5" data-testid="historique-page">
      <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Historique 2024 → 2026</h1>

      <Card>
        <CardHeader><CardTitle>Importer une évaluation historique 2024</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger data-testid="import-site-select"><SelectValue placeholder="Sélectionner le site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}</SelectContent>
          </Select>
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground cursor-pointer"
            data-testid="import-dropzone"
            onClick={() => document.getElementById("import-file-input").click()}
          >
            <UploadCloud className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            {file ? file.name : "Glissez-déposez le fichier XLSX ou cliquez pour sélectionner"}
            <input id="import-file-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
          </div>
          <Button data-testid="import-preview-button" onClick={runPreview} disabled={!file}>Analyser le fichier</Button>

          {preview && (
            <div className="space-y-2 text-sm border-t pt-3">
              <div>Lignes détectées: <b>{preview.nb_lignes_detectees}</b></div>
              <div>Anomalies (références manquantes): <b>{preview.nb_anomalies}</b></div>
              <div>Onglets ignorés: {preview.sheets_ignored.join(", ") || "Aucun"}</div>
              <Button data-testid="import-confirm-button" className="bg-emerald-600 hover:bg-emerald-700" disabled={!siteId} onClick={confirmImport}>
                Confirmer l'import pour ce site
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Comparaison par site</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger data-testid="comparison-site-select"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}</SelectContent>
          </Select>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {comparison.map((c) => (
              <div key={c.requirement_id} className="flex items-center justify-between text-xs border-b py-1 last:border-0">
                <span className="font-mono">{c.reference}</span>
                <span>{c.score_2024 ?? "-"} → {c.score_2026 ?? "-"}</span>
                <Badge variant="outline" className={
                  c.evolution === "AMELIORATION" ? "text-emerald-600" :
                  c.evolution === "REGRESSION" ? "text-rose-600" : ""
                }>{c.evolution || "-"}</Badge>
              </div>
            ))}
            {comparison.length === 0 && <p className="text-sm text-muted-foreground">Sélectionnez un site pour voir la comparaison.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
